import * as wjCore from '@grapecity/wijmo';
import { Menu } from '@grapecity/wijmo.input';
import { Popover } from 'antd';
import { assign, cloneDeep } from 'lodash';
import React, { useState } from 'react';

import AbilityService from '../services/ability.service';
import { fetchSignedUrl, uploadDocumentToAzure } from '../services/uploadfiles.service';

export const getRowNumber = (data, key) => {
  const index = data.findIndex((item) => item.key === key);
  return index <= -1 ? 1 : index + 1;
};

export const isHiddenRows = (allData, filteredData, error) => {
  const errorResponse = error?.response ? JSON.parse(error?.response) : '';
  let errors = errorResponse?.error || [];

  const filteredAllData = cloneDeep(allData);

  let isKeyHidden = false;
  errors = errors.map((error) => {
    const filteredAllDataIndex = filteredAllData?.findIndex(
      (item) => item?.oldKey && item?.key === error?.key,
    );
    const currentItem = cloneDeep(filteredAllData[filteredAllDataIndex]);
    filteredAllData.splice(filteredAllDataIndex, 1);
    const isItemPresent = filteredData?.find((item) => item?.oldKey === currentItem?.oldKey);
    if (!isItemPresent) {
      error.key = 'hidden';
      isKeyHidden = true;
    }
    return error;
  });

  errors = errors
    ?.map(
      (item) =>
        `${item?.key === 'hidden' ? 'At Hidden Row (Filtered)' : `At Row ${getRowNumber(filteredData, item?.key)} `}: ${item?.reason}`,
    )
    ?.slice(0, 3)
    ?.join(';\n');
  return { isKeyHidden, errors };
};

export const getErrorMessage = (error, data, allData, isFilterApplied) => {
  const errorResponse = error?.response ? JSON.parse(error?.response) : '';
  if (!data || !data?.length) {
    if (errorResponse?.error?.length) {
      return errorResponse?.error?.map((item) => `${item?.reason}`)?.join('\n');
    }
    return errorResponse?.message;
  }
  if (isFilterApplied) {
    const { isKeyHidden, errors } = isHiddenRows(allData, data, error);
    if (isKeyHidden) {
      return errors;
    }
  }
  return (
    errorResponse?.error
      ?.map((item) => `At Row ${getRowNumber(data, item?.key)} : ${item?.reason}`)
      ?.slice(0, 3)
      ?.join(';\n') || errorResponse?.message
  );
};

export const checkPermission = (moduleName, permission, screenName = 'any', field = '') => {
  return AbilityService.ability.can(
    `${moduleName}.action.${permission}`,
    `${moduleName}.screen.${screenName}`,
    field,
  );
};

export const downloadFileToSystem = async (fileUrl, fileName) => {
  await fetch(fileUrl)
    .then((response) => response.blob())
    .then((blob) => {
      // Create a URL for the blob data
      const blobUrl = window.URL.createObjectURL(blob);

      // Create an anchor element
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = fileName;

      // Simulate a click on the anchor element
      anchor.click();

      // Clean up by revoking the blob URL and removing the anchor element
      window.URL.revokeObjectURL(blobUrl);
    });
};

export const downloadFileResponse = async (fileUrl) => {
  return await fetch(fileUrl)
    .then((response) => response.blob())
    .then((blob) => {
      // Create a URL for the blob data
      const blobUrl = window.URL.createObjectURL(blob);

      return blobUrl;
    });
};

export const extractFileNameFromURL = (url) => {
  // Split the URL by '/'
  const parts = url.split('/');

  // Get the last part of the URL which is the file name
  const fileNameWithTimestamp = parts[parts.length - 1];

  // Split the file name by underscore to separate timestamp and file name
  const fileNameParts = fileNameWithTimestamp.split('_');

  // Get the last part of the file name parts which is the actual file name
  const fileName = fileNameParts[fileNameParts.length - 1];

  return fileName;
};

export const refreshNewAddedItem = (s, row, item) => {
  // console.log(s.collectionView.currentEditItem);
  // console.log(Object.keys(s.collectionView.currentEditItem).length > 0);
  // console.log(row.index === 0);
  // console.log(s.newRowAtTop);
  // console.log(s.allowAddNew);
  if (s.collectionView.currentAddItem) {
    s.collectionView.commitNew();
  } else if (
    s.collectionView.currentEditItem &&
    Object.keys(s.collectionView.currentEditItem).length > 0 &&
    row.index === 0 &&
    s.newRowAtTop &&
    s.allowAddNew
  ) {
    const newItem = s.collectionView.addNew();
    for (const k in item) {
      newItem[k] = item[k];
    }
    row.dataItem = null;
    s.collectionView.cancelEdit();
    s.collectionView.commitNew();

    // var sel = s.selection;
    // var rowI = s.rows.length;
    // // select next cell if the entered data is valid
    // console.log(sel.row)
    // s.select(1, sel.col + 1);
  } else if (s.collectionView.currentEditItem) {
    s.collectionView.commitEdit();
  }
  return s;
};

export const findModifiedFields = (originalObject, newObject) => {
  return Object.keys(originalObject).filter((key) => originalObject[key] !== newObject[key]);
};

export const formatValue = (inputValue) => {
  const number = parseFloat(inputValue);
  if (!isNaN(number) && isFinite(number)) {
    const formattedNumber = number.toFixed(2);
    return parseFloat(formattedNumber);
  }
  return inputValue;
};

// Round numbers to 2 decimal places
// Example: 2.9999 -> 3.00, 1.456 -> 1.46, 1.454 -> 1.45
export const roundToTwoDecimals = (value) => {
  return Math.round((parseFloat(value) || 0) * 100) / 100;
};

export const formatIntValue = (inputValue) => {
  const number = parseInt(inputValue, 10);
  if (!isNaN(number) && isFinite(number)) {
    const formattedNumber = number.toFixed(2);
    return parseInt(formattedNumber, 10);
  }
  return inputValue;
};

export const getFileSize = (size) => {
  if (size < 1000) {
    return `${size} B`;
  }
  if (size >= 1000 && size < 1000000) {
    const sizeInKB = size / 1000;
    return `${sizeInKB % 1 === 0 ? sizeInKB.toFixed(0) : sizeInKB.toFixed(1)} KB`;
  }
  const sizeInMB = size / 1000000;
  return `${sizeInMB % 1 === 0 ? sizeInMB.toFixed(0) : sizeInMB.toFixed(1)} MB`;
};

export const getSignedUrl = async (file, entityType = 'capex_requests') => {
  try {
    const payload = {
      fileName: file.name,
      entityType,
    };
    const response = await fetchSignedUrl(payload);
    const { surl, purl } = response?.data?.data;
    if (surl) {
      await uploadDocumentToAzure(surl, file, file.type);
      return { status: true, purl };
    }
  } catch (error) {
    return { status: false, purl: '' };
  }
  return { status: false, purl: '' };
};

export const isAllSelectionEmptyAfterFilter = (selectedFilterItems) => {
  let isAllSelectionEmptyAfterFilter = true;
  for (let index = 0; index < Object.keys(selectedFilterItems?.current)?.length; index++) {
    if (selectedFilterItems?.current?.[index]?.length) {
      isAllSelectionEmptyAfterFilter = false;
    }
  }
  return isAllSelectionEmptyAfterFilter;
};

export const INVALID_CHARS = [
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
];

export const referenceItems = [
  'reference1Name',
  'reference2Name',
  'reference3Name',
  'reference4Name',
  'reference5Name',
];
export const referenceKeys = ['reference1', 'reference2', 'reference3', 'reference4', 'reference5'];

export const getUpdatedItemWithReference = (item) => {
  let internalVendorKey = 'reference1';
  for (let i = referenceKeys.length; i > 1; i--) {
    const currentKey = referenceKeys[i];
    const prevKey = referenceKeys[i - 1];
    if (Boolean(item[currentKey]) && !item[prevKey]) {
      item[prevKey] = item[currentKey];
      item[currentKey] = null;
    }
    if (Boolean(item[prevKey]) && item?.[prevKey]?.isInternalVendor) {
      internalVendorKey = prevKey;
    }
  }
  // Assign reference1 as Provision Vendor
  assign(item, { reference1: item?.[internalVendorKey], [internalVendorKey]: item?.reference1 });
  const referenceArray = [
    item.reference1,
    item.reference2,
    item.reference3,
    item.reference4,
    item.reference5,
  ];
  const allReference = referenceArray.filter((value) => Boolean(value));
  item.reference1 = allReference[0];
  item.reference2 = allReference[1] || null;
  item.reference3 = allReference[2] || null;
  item.reference4 = allReference[3] || null;
  item.reference5 = allReference[4] || null;
  return item;
};

const brandNames = {
  'Provision 5Star': 'Provision 5 Star',
  'Provision 4Star': 'Provision 4 Star',
  'Provision 3Star': 'Provision 3 Star',
};

export const mappedBrandNames = (name) => {
  return brandNames?.[name] || name;
};

const starTypeNames = {
  '5 STAR': '5 ⭐',
  '4 STAR': '4 ⭐',
  '3 STAR': '3 ⭐',
};

export const mappedStarTypeNames = (name) => {
  return starTypeNames?.[name] || name;
};

export const TOGGLE_FORMATTING_STATES = {
  REFERENCE_IMAGE: 'REFERENCE_IMAGE',
  WRAP_TEXT: 'WRAP_TEXT',
  LEFT_CENTER_ALIGN: 'LEFT_CENTER_ALIGN',
  PAGE_VIEW_OR_LIST_VIEW: 'PAGE_VIEW_OR_LIST_VIEW',
  ANALYSIS_PAGE_REFERENCE_IMAGE: 'ANALYSIS_PAGE_REFERENCE_IMAGE',
  BUDGET_DETAIL_PAGE_REFERENCE_IMAGE: 'BUDGET_DETAIL_PAGE_REFERENCE_IMAGE',
};

export const getToggleFormattingState = (stateName) => {
  return localStorage.getItem(stateName) === 'true';
};

export const manageToggleFormattingStates = (stateName) => {
  let stateValue = localStorage.getItem(stateName);
  if (stateValue === 'true' || stateValue === true) {
    localStorage.setItem(stateName, false);
    stateValue = false;
  } else if (!stateValue || stateValue === 'false' || stateValue === false) {
    localStorage.setItem(stateName, true);
    stateValue = true;
  }
  return stateValue;
};

export const convertToThumb = (url) => {
  if (!url) return '';
  // Extract the directory path and file name
  const pathParts = url.split('/');
  const fileName = pathParts.pop(); // Get the last part, which is the file name

  // Split the file name into base name and extension
  const [name, ext] = fileName.split('.');

  // Construct the new thumb file name
  const thumbFileName = `${encodeURIComponent(`${name}.${ext}`)}`;

  // Join the directory path with the new file name
  pathParts.push(thumbFileName);

  // Return the modified URL
  return `${process.env.REACT_APP_FILE_BASE_URL}/${pathParts.join('/')}?time=${new Date().getTime()}`;
};

export const convertToMain = (url, isImageChanged = false) => {
  if (!url) return '';
  // Extract the directory path and file name
  const pathParts = url.split('/');
  const fileName = pathParts.pop(); // Get the last part, which is the file name

  // Split the file name into base name and extension
  const [name, ext] = fileName.split('.');

  // Construct the new thumb file name
  const thumbFileName =
    url?.includes('temp') || isImageChanged ? `${fileName}` : `${name}_main.${ext}`;

  // Join the directory path with the new file name
  pathParts.push(thumbFileName);

  // Return the modified URL
  if (url?.includes(process.env.REACT_APP_FILE_BASE_URL)) {
    return pathParts.join('/');
  }
  return `${process.env.REACT_APP_FILE_BASE_URL}/${pathParts.join('/')}`;
};

export const checkProductImageFileSize = (file, _imageFields) => {
  return file.size / 1024 / 1024 <= 2;
};

export const getCellForHistory = (colName, Item, data, slugMapper) => {
  if (Boolean(Item?.[colName]?.isChanged)) {
    return (
      <Popover
        key={Item?.id + colName}
        content={
          <div>
            <p>{`Replaced '${slugMapper ? slugMapper[Item?.[colName]?.before] : Item?.[colName]?.before}' with '${slugMapper ? slugMapper[Item?.[colName]?.after] : Item?.[colName]?.after}' `}</p>
          </div>
        }
        trigger='hover'
      >
        <td className='ant-table-cell col-color' id={`cell_history_${Item?.id}`}>
          {slugMapper ? slugMapper[data] : data}
        </td>
      </Popover>
    );
  }
  return <td className='ant-table-cell'>{slugMapper ? slugMapper[data] : data}</td>;
};

export const checkDocumentFileSize = (file) => {
  return file.size / 1024 / 1024 <= 50;
};

export const buildContextMenu = (grid, imageFields = [], changeUnsave, processRecords) => {
  const menu = new Menu(document.createElement('div'), {
    owner: grid.hostElement,
    displayMemberPath: 'header',
    subItemsPath: 'items',
    commandParameterPath: 'cmd',
    dropDownCssClass: 'ctx-menu',
    openOnHover: true,
    closeOnLeave: true,
    itemsSource: [{ header: 'Delete', cmd: 'REMOVE' }],
    command: {
      canExecuteCommand: (cmd) => {
        switch (cmd) {
          case 'REMOVE':
            return true;
          default:
            return true;
        }
      },
      executeCommand: (cmd) => {
        const { rows } = grid;
        const row = rows[grid.selection.row];
        switch (cmd) {
          case 'REMOVE': {
            const item = row?.dataItem;
            imageFields.forEach((element) => {
              item[element] = '';
            });

            if (processRecords) {
              processRecords(item);
            }

            if (changeUnsave) {
              changeUnsave();
            }
            item.isEditing = true;
            break;
          }
          default:
            break;
        }
        grid.refresh();
        const sel = grid.selection;
        const cell = grid.cells.getCellElement(sel.row, sel.col);
        if (cell) {
          cell.focus();
        }
      },
    },
  });
  return menu;
};

export const getGridDiaplayValue = (grid) => {
  return Array.from({ length: grid.rows.length }, (_, r) => {
    const row = {};
    grid.columns.forEach((col) => {
      if (col.binding) {
        row[col.binding] = grid.getCellData(r, col.index, true);
      }
    });
    return row;
  });
};

export const productionStatusMap = {
  A: 'Approved',
  B: 'Approved with Comments',
  C: 'Revise & Resubmit',
  D: 'Rejected',
  E: 'Not Required',
  F: 'In Progress',
  G: 'Not in Scope',
};

export const colorMap = {
  A: 'green',
  B: 'green',
  C: 'red',
  D: 'red',
  E: 'green',
  F: '#FFBF00',
  G: 'green',
};

export const applyDefaultFilter = (filterRef, filters) => {
  filters.forEach((eachFilter) => {
    const columnFilter = filterRef.current.getColumnFilter(eachFilter.column);
    columnFilter.valueFilter.showValues = { [eachFilter.value]: true };
    columnFilter.valueFilter.showAllValues = false; //
  });
  filterRef.current.apply();
  filterRef.current?.grid?.collectionView?.refresh();
};

export const useAsyncReference = (value) => {
  const ref = React.useRef(value);
  const [, forceRender] = useState(false);

  const updateState = (newState) => {
    ref.current = newState;
    forceRender((s) => !s);
  };

  return [ref, updateState];
};

export const initializeImageFilter = (filterRef, columnBinding) => {
  const imageColFilter = filterRef.current?.getColumnFilter(columnBinding);
  if (imageColFilter) {
    const valFilter = imageColFilter.valueFilter;
    valFilter.uniqueValues = ['No Image', 'Image Added'];

    valFilter.sortValues = false;
    valFilter.apply = function (value) {
      const col = this.column;
      if (!col || !col._binding || !this._values || !Object.keys(this._values).length) {
        return true;
      }
      //
      value = col._binding.getValue(value);
      value = this.dataMap
        ? this.dataMap.getDisplayValue(value) || ''
        : col.dataMap
          ? col.dataMap.getDisplayValue(value) || ''
          : wjCore.Globalize.format(value, col.format);
      return Boolean(
        (this.showValues['No Image'] && !value) || (this.showValues['Image Added'] && value),
      );
    };
  }
};

export const initializeFilterMaxValues = (filterRef, columnBindings = []) => {
  if (filterRef?.current && columnBindings.length) {
    columnBindings.forEach((binding) => {
      const colFilter = filterRef.current.getColumnFilter(binding);
      if (colFilter) {
        colFilter.valueFilter.maxValues = 1000000;
      }
    });
  }
};

// Helper: convert base64 string → Uint8Array
export function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// Helper: fetch image URL → base64 (no prefix)
export async function fetchImageBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      resolve(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(blob);
  });
}
